import { useState, type FormEvent } from "react";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { siteMeta } from "@/mocks/site";

const FORM_SUBMIT_URL = "https://readdy.ai/api/form/dahgqgjoh653ivfvnusg";

const origin = "https://youhuochigua.example";

type Status = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot.length > 0) {
      setStatus("success");
      setFormError("");
      formEl.reset();
      return;
    }

    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText) as typeof parsed;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        responseText ||
        "";

      const isSpam = serverMsg.toLowerCase().includes("spam");
      const codeSuccess =
        typeof parsed?.code === "string" && /^(ok|success)$/i.test(parsed.code);

      if (!response.ok || isSpam || !codeSuccess) {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过内容校验，请调整后再试一次。"
            : serverMsg.length > 0
              ? serverMsg
              : "提交失败，请稍后重试。",
        );
        return;
      }

      setStatus("success");
      setFormError("");
      formEl.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常导致提交失败，请检查网络后重试。");
    }
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系我们 - 诱惑吃瓜",
      url: `${origin}/contact`,
      inLanguage: "zh-CN",
      description:
        "通过留言表单或邮件联系诱惑吃瓜，提交内容纠错、材料线索或合作咨询。",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteMeta.name,
      url: origin,
      email: siteMeta.email,
    },
  ];

  return (
    <>
      <Seo
        title="联系我们 - 诱惑吃瓜 内容纠错与材料线索"
        description="有内容需要纠错，或者手上有可以核实的一手材料线索？通过留言表单或邮件联系诱惑吃瓜，我们会核对后更新对应的复盘文章。"
        keywords="诱惑吃瓜,联系我们,内容纠错,材料线索,合作咨询"
        path="/contact"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main className="bg-background-100">
        <header className="border-b border-background-200 bg-background-50 px-4 pb-8 pt-28 md:px-8 md:pb-10 md:pt-32 lg:px-12">
          <div className="mx-auto max-w-6xl">
            <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "联系我们" }]} />
            <h1 className="mt-4 font-heading text-2xl font-bold leading-tight tracking-tight text-foreground-950 md:text-4xl">
              联系我们
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              内容纠错、材料线索、合作咨询都可以从这里发起。附上材料来源会让我们的核实效率高很多。
            </p>
          </div>
        </header>

        <div className="px-4 py-10 md:px-8 md:py-14 lg:px-12">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
            <aside className="lg:col-span-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  联系方式
                </h2>
                <ul className="mt-5 space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-700">
                      <i className="ri-mail-line" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">邮箱</span>
                      <span className="block text-sm font-medium text-foreground-900">
                        {siteMeta.email}
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-lg text-secondary-700">
                      <i className="ri-time-line" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">回复时效</span>
                      <span className="block text-sm font-medium text-foreground-900">
                        工作日 48 小时内回复
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-700">
                      <i className="ri-shield-check-line" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">材料处理</span>
                      <span className="block text-sm font-medium text-foreground-900">
                        仅采用可回溯来源
                      </span>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  提交线索的小建议
                </h2>
                <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-foreground-600">
                  <li className="flex gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"></span>
                    尽量附上原始出处，例如官方发布页、节目正片链接。
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"></span>
                    截图请保留完整界面信息，方便我们核对发布时间。
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"></span>
                    涉及私人隐私与未成年人的内容我们不会收录，抱歉。
                  </li>
                </ul>
              </div>
            </aside>

            <div className="lg:col-span-7">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  给我们留言
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  带 * 的为必填项，留言内容请控制在 500 字以内。
                </p>

                {status === "success" && (
                  <div
                    role="status"
                    className="mt-5 flex items-start gap-3 rounded-md border border-secondary-300 bg-secondary-50 p-4"
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center text-lg text-secondary-700">
                      <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                    </span>
                    <p className="text-sm leading-relaxed text-secondary-900">
                      已收到你的留言，感谢提供线索。我们会尽快核对，如需补充信息会通过你填写的邮箱联系。
                    </p>
                  </div>
                )}

                {status === "error" && formError.length > 0 && (
                  <div
                    role="alert"
                    className="mt-5 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 p-4"
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center text-lg text-primary-700">
                      <i className="ri-error-warning-line" aria-hidden="true"></i>
                    </span>
                    <p className="text-sm leading-relaxed text-primary-900">
                      {formError}
                    </p>
                  </div>
                )}

                <form
                  data-readdy-form
                  id="contact-form-youhuochigua"
                  onSubmit={handleSubmit}
                  className="mt-5 space-y-4"
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-sm font-medium text-foreground-800"
                      >
                        称呼 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        autoComplete="name"
                        placeholder="怎么称呼你"
                        className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-sm font-medium text-foreground-800"
                      >
                        邮箱 <span className="text-primary-600">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        placeholder="用于接收我们的回复"
                        className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="contact-topic"
                      className="block text-sm font-medium text-foreground-800"
                    >
                      事项类型 <span className="text-primary-600">*</span>
                    </label>
                    <select
                      id="contact-topic"
                      name="topic"
                      required
                      defaultValue=""
                      className="mt-2 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    >
                      <option value="" disabled>
                        请选择事项类型
                      </option>
                      <option value="内容纠错">内容纠错</option>
                      <option value="材料线索">提供材料线索</option>
                      <option value="合作咨询">合作咨询</option>
                      <option value="其他建议">其他建议</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="contact-message"
                      className="block text-sm font-medium text-foreground-800"
                    >
                      留言内容 <span className="text-primary-600">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={6}
                      maxLength={500}
                      placeholder="请描述具体事件、需要核实的信息，以及可回溯的来源（500 字以内）"
                      className="mt-2 w-full resize-y rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    className="assistive-input-zone"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    defaultValue=""
                  />

                  <div className="flex flex-col gap-3 border-t border-background-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs leading-relaxed text-foreground-500">
                      提交即表示你同意我们为核实内容而使用你提供的信息。
                    </p>
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {status === "submitting" ? "提交中…" : "提交留言"}
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-send-plane-fill" aria-hidden="true"></i>
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}